import { Injectable, WritableSignal, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, catchError, map, of, shareReplay } from 'rxjs';
import * as Sentry from '@sentry/browser';

import { environment } from '@environments/environment';
import { serverEnvironment } from '@environments/environment.server';
import { GlobalsService } from '@services/globals.service';
import { SsrService } from '@services/ssr.service';
import { RecommenderResponse } from '@models/recommender.model';
import { ListDataResponse, ListItem } from '@models/list.model';

@Injectable({
  providedIn: 'root',
})
export class RecommenderService {
  private http = inject(HttpClient);
  private globalsService = inject(GlobalsService);
  private ssrService = inject(SsrService);
  private limit: number = 10;
  public recommenderDefaultData: WritableSignal<ListItem[] | undefined> = signal(undefined);
  public recommenderTemporaryData: WritableSignal<ListItem[] | undefined> = signal(undefined);
  public recommenderNewCarsData: WritableSignal<ListItem[] | undefined> = signal(undefined);
  public recommenderComingSoonData: WritableSignal<ListItem[] | undefined> = signal(undefined);

  getRecommenderData(): Observable<ListItem[]> {
    if (this.recommenderDefaultData()) return of(this.recommenderDefaultData() as ListItem[]);

    return this.http.get<RecommenderResponse>(`${environment.recommenderApi}?limit=${this.limit}`, { headers: this.globalsService.headerHash() }).pipe(
      map((response) => {
        this.recommenderDefaultData.set(response.message);
        response.message.forEach((item) => {
          item.photos.default[0].url[0] = this.globalsService.replacePlaceholderPhoto(item.photos.default[0].url[0], '300x227x100');
        });
        return response.message;
      }),
      shareReplay(1),
      catchError((e) => {
        const timestamp: number = new Date().getTime();
        Sentry.captureMessage('Nepodařilo se získat data recommenderu - Timestamp: ' + timestamp + ' Error: ' + e.message, { level: 'fatal', extra: { limit: this.limit } });
        throw e;
      }),
    );
  }

  getTemporaryRecommenderData(priceFrom: number): Observable<ListItem[]> {
    return this.http.post<ListDataResponse>(`${this.ssrService.isServer() ? serverEnvironment.carsApi : environment.carsApi}?withEquipCat=1&limit=${this.limit}&skip=0&sort=2`, { priceFrom: priceFrom, leasingNumberOfMonth: 60 }, { headers: this.globalsService.headerHash() }).pipe(
      map((response) => {
        response.message.items.forEach((item) => {
          item.photos.default[0].url[0] = this.globalsService.replacePlaceholderPhoto(item.photos.default[0].url[0], '300x227x100');
        });
        this.recommenderTemporaryData.set(response.message.items);
        return response.message.items;
      }),
      shareReplay(1),
    );
  }

  getNewCarsRecommenderData(): Observable<ListItem[]> {
    return this.http
      .get<ListDataResponse>(
        `${environment.newCarsRecommenderApi}`,
      )
      .pipe(
        map((response) => {
          response.message.items.forEach((item) => {
            item.photos.default[0].url[0] = this.globalsService.replacePlaceholderPhoto(item.photos.default[0].url[0], '300x227x100');
          });
          this.recommenderNewCarsData.set(response.message.items);
          return response.message.items;
        }),
        shareReplay(1),
      );
  }

  getComingSoonRecommender(): Observable<ListItem[]> {
    return this.http.get<ListDataResponse>(`${environment.comingSoonRecommenderApi}`).pipe(
      map((response) => {
        response.message.items.forEach((item) => {
          item.photos.default[0].url[0] = this.globalsService.replacePlaceholderPhoto(item.photos.default[0].url[0], '300x227x100');
        });
        this.recommenderComingSoonData.set(response.message.items);
        return response.message.items;
      }),
      shareReplay(1),
    );
  }
}
